import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { User, Lock, Eye, EyeOff, ArrowLeft, Shield, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Profile = () => {
  const { user, token, logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  const t = (key) => {
    const translations = {
      profile: { pt: 'Perfil', en: 'Profile' },
      changePassword: { pt: 'Alterar Palavra-passe', en: 'Change Password' },
      currentPassword: { pt: 'Palavra-passe Atual', en: 'Current Password' },
      newPassword: { pt: 'Nova Palavra-passe', en: 'New Password' },
      confirmPassword: { pt: 'Confirmar Nova Palavra-passe', en: 'Confirm New Password' },
      save: { pt: 'Guardar', en: 'Save' },
      back: { pt: 'Voltar', en: 'Back' },
      passwordChanged: { pt: 'Palavra-passe alterada com sucesso!', en: 'Password changed successfully!' },
      passwordMismatch: { pt: 'As palavras-passe não coincidem', en: 'Passwords do not match' },
      passwordTooShort: { pt: 'A palavra-passe deve ter pelo menos 6 caracteres', en: 'Password must be at least 6 characters' },
      fillAllFields: { pt: 'Preencha todos os campos', en: 'Please fill all fields' },
      error: { pt: 'Erro ao alterar palavra-passe', en: 'Error changing password' },
      wrongPassword: { pt: 'Palavra-passe atual incorreta', en: 'Current password is incorrect' },
      accountInfo: { pt: 'Informações da Conta', en: 'Account Information' },
      email: { pt: 'Email', en: 'Email' },
      role: { pt: 'Tipo de Conta', en: 'Account Type' },
      roleAdmin: { pt: 'Administrador', en: 'Administrator' },
      roleUser: { pt: 'Utilizador', en: 'User' },
      roleITTalent: { pt: 'IT Talent', en: 'IT Talent' },
      roleRecruiter: { pt: 'Recrutador', en: 'Recruiter' },
      securityNote: { pt: 'A sua palavra-passe é encriptada e segura. Nunca a partilhamos com terceiros.', en: 'Your password is encrypted and secure. We never share it with third parties.' },
    };
    return translations[key]?.[language] || key;
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return t('roleAdmin');
      case 'it_talent': return t('roleITTalent');
      case 'recruiter': return t('roleRecruiter');
      default: return t('roleUser');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    // Validations
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error(t('fillAllFields'));
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error(t('passwordTooShort'));
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error(t('passwordMismatch'));
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/user/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(t('passwordChanged'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(t('wrongPassword'));
      } else {
        toast.error(t('error'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] py-8 px-4" data-testid="profile-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-[#64748b]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('back')}
          </Button>
        </div>

        <div className="space-y-6">
          {/* Account Info Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1E3C3C]">
                <User className="w-5 h-5" />
                {t('accountInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-[#f0fdfa] rounded-lg">
                <Mail className="w-5 h-5 text-[#1E3C3C]" />
                <div>
                  <p className="text-xs text-[#64748b]">{t('email')}</p>
                  <p className="font-medium text-[#0f172a]">{user?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-[#f0fdfa] rounded-lg">
                <Shield className="w-5 h-5 text-[#1E3C3C]" />
                <div>
                  <p className="text-xs text-[#64748b]">{t('role')}</p>
                  <p className="font-medium text-[#0f172a]">{getRoleLabel(user?.role)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Change Password Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1E3C3C]">
                <Lock className="w-5 h-5" />
                {t('changePassword')}
              </CardTitle>
              <CardDescription className="text-[#64748b]">
                {t('securityNote')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                {/* Current Password */}
                <div>
                  <Label htmlFor="currentPassword">{t('currentPassword')}</Label>
                  <div className="relative mt-1">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="pr-10"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#0f172a]"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <Label htmlFor="newPassword">{t('newPassword')}</Label>
                  <div className="relative mt-1">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pr-10"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#0f172a]"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
                  <div className="relative mt-1">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pr-10"
                      data-testid="confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-[#0f172a]"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={saving}
                  className="w-full bg-[#1E3C3C] hover:bg-[#0F766E] text-white"
                  data-testid="save-password-btn"
                >
                  {saving ? (
                    <div className="loading-spinner w-5 h-5"></div>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      {t('save')}
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;
